/*
 * HotWaterTileRenderer — one DHW storage as an operator tile.
 *
 * Hero: top-of-tank temperature (the number that answers "is there hot
 * water?"). A slim stratification bar on the right shows top/mid/bottom
 * segments coloured by temperature so a glance tells the whole story.
 * Setpoint stepper below the hero, status chip in the header, chip for
 * "next Legionella cycle" in the footer.
 */

import { useState } from 'preact/hooks';
import type { HotWaterTileWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import {
  usePoint,
  useOptionalPoint,
  numeric,
  quality,
  clamp,
  formatRelativeDays,
} from './_compound-helpers';
import { useLeafMeta } from '../LeafMetaContext';
import { formatNumber } from '../../format/leafValue';
import './HotWaterTileRenderer.css';

interface Props {
  widget: HotWaterTileWidget;
  source: IPointBindingSource;
}

const SETPOINT_MIN = 30;
const SETPOINT_MAX = 65;
const SETPOINT_STEP = 1;

export function HotWaterTileRenderer({ widget, source }: Props) {
  const top = usePoint(source, widget.topTemperaturePoint);
  const mid = useOptionalPoint(source, widget.midTemperaturePoint);
  const bottom = useOptionalPoint(source, widget.bottomTemperaturePoint);
  const setpoint = usePoint(source, widget.setpointPoint);
  const setpointMeta = useLeafMeta(widget.setpointPoint);
  const state = useOptionalPoint(source, widget.statePoint);
  const nextLegionella = useOptionalPoint(source, widget.nextLegionellaPoint);

  const topVal = numeric(top.value.value?.value);
  const midVal = numeric(mid?.value.value?.value);
  const bottomVal = numeric(bottom?.value.value?.value);
  const setpointVal = numeric(setpoint.value.value?.value);

  const topStale = quality(top.value.value) !== 'ok';
  const setpointStale = quality(setpoint.value.value) !== 'ok';
  const tileStale = topStale && setpointStale;

  // Read-only when the author marked it so or the leaf is not writable per
  // schema. Then there are no +/- buttons at all; disabled ones look like a
  // fault rather than a display.
  const readOnly = widget.setpointWritable === false || setpointMeta?.writable === false;
  const setpointWritable = !readOnly;

  const [pendingSetpoint, setPendingSetpoint] = useState<number | null>(null);
  const [pendingState, setPendingState] = useState<'sending' | 'rejected' | null>(null);

  async function commitSetpoint(target: number) {
    if (!setpointWritable) return;
    setPendingSetpoint(target);
    setPendingState('sending');
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.setpointPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPendingSetpoint(null);
          setPendingState(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPendingState('rejected');
          return;
        }
      }
      setPendingSetpoint(null);
      setPendingState(null);
    } catch {
      setPendingSetpoint(null);
      setPendingState(null);
    }
  }

  const shownSetpoint = pendingSetpoint ?? setpointVal;

  const stateInfo = classifyState(state?.value.value?.value);
  const legionellaText = formatRelativeDays(nextLegionella?.value.value?.value);

  return (
    <div class={`hv-hw-tile${tileStale ? ' hv-hw-tile--stale' : ''}`}>
      <header class="hv-hw-tile__head">
        <div class="hv-hw-tile__title">
          <div class="hv-hw-tile__name">{widget.storageName}</div>
          {widget.label && <div class="hv-hw-tile__label">{widget.label}</div>}
        </div>
        {state && (
          <span class={`hv-hw-tile__state hv-hw-tile__state--${stateInfo.tone}`}>
            {stateInfo.label}
          </span>
        )}
      </header>

      <div class="hv-hw-tile__body">
        <div class="hv-hw-tile__left">
          <div class="hv-hw-tile__hero">
            <div class="hv-hw-tile__hero-value">
              {topVal != null ? formatNumber(topVal, 1) : '—'}<span>°C</span>
            </div>
            <div class="hv-hw-tile__hero-label">Speicher oben</div>
          </div>

          <div class="hv-hw-tile__setpoint">
            {!readOnly && (
              <button
                type="button"
                class="hv-hw-tile__step"
                onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint - SETPOINT_STEP, SETPOINT_MIN, SETPOINT_MAX))}
                disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
                aria-label="Sollwert verringern"
              >−</button>
            )}
            <div class="hv-hw-tile__setpoint-value">
              <span class="hv-hw-tile__setpoint-num">
                {shownSetpoint != null ? formatNumber(shownSetpoint, 0) : '—'}
              </span>
              <span class="hv-hw-tile__setpoint-unit">°C</span>
              {pendingState === 'sending' && <span class="hv-hw-tile__pending">…</span>}
              {pendingState === 'rejected' && <span class="hv-hw-tile__rejected">✕</span>}
            </div>
            {!readOnly && (
              <button
                type="button"
                class="hv-hw-tile__step"
                onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint + SETPOINT_STEP, SETPOINT_MIN, SETPOINT_MAX))}
                disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
                aria-label="Sollwert erhöhen"
              >+</button>
            )}
          </div>
        </div>

        <StratificationBar top={topVal} mid={midVal} bottom={bottomVal} />
      </div>

      {legionellaText && (
        <footer class="hv-hw-tile__foot">
          <span class="hv-hw-tile__stat">
            <span class="hv-hw-tile__stat-label">Legionellen</span>
            <span class="hv-hw-tile__stat-value">{legionellaText}</span>
          </span>
        </footer>
      )}
    </div>
  );
}

// ---------- Stratification bar ------------------------------------------

/**
 * Vertical bar of three coloured segments — top/mid/bottom of the tank.
 * Missing sensors just skip that segment. Hotter → redder ramp.
 */
function StratificationBar({
  top,
  mid,
  bottom,
}: {
  top: number | null;
  mid: number | null;
  bottom: number | null;
}) {
  const segments: { label: string; value: number | null }[] = [
    { label: 'oben', value: top },
    { label: 'mitte', value: mid },
    { label: 'unten', value: bottom },
  ];
  const present = segments.filter((s) => s.value != null);
  if (present.length === 0) return null;

  const height = 96;
  const width = 22;
  const step = height / present.length;

  return (
    <svg class="hv-hw-tile__strat" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Speicherschichtung">
      <rect x="0" y="0" width={width} height={height} rx="3" ry="3" class="hv-hw-tile__strat-frame" />
      {present.map((s, i) => (
        <g key={s.label}>
          <rect
            x="1"
            y={i * step + 1}
            width={width - 2}
            height={step - 2}
            fill={temperatureColour(s.value!)}
          />
          <title>{`${s.label}: ${formatNumber(s.value!, 1)}°C`}</title>
        </g>
      ))}
    </svg>
  );
}

/** Ramp from cool blue at 20°C to hot red at 65°C. Clamped at edges. */
function temperatureColour(t: number): string {
  const lo = 20;
  const hi = 65;
  const norm = (t - lo) / (hi - lo);
  const clamped = norm < 0 ? 0 : norm > 1 ? 1 : norm;
  // hue 220 (blue) → 0 (red)
  const hue = 220 - clamped * 220;
  const sat = 65;
  const lightness = 50;
  return `hsl(${hue.toFixed(0)}, ${sat}%, ${lightness}%)`;
}

// ---------- State chip ---------------------------------------------------

interface StateInfo { label: string; tone: 'idle' | 'charging' | 'legionella' | 'fault' | 'unknown'; }

function classifyState(v: unknown): StateInfo {
  const raw = String(v ?? '').trim();
  const s = raw.toLowerCase();
  if (s === '' || s === 'null' || s === 'undefined') return { label: '—', tone: 'unknown' };
  if (s.includes('fault') || s.includes('stoerung') || s.includes('störung'))
    return { label: raw || 'Fehler', tone: 'fault' };
  if (s.includes('legionella') || s.includes('legionellen'))
    return { label: raw || 'Legionella', tone: 'legionella' };
  if (s.includes('charg') || s.includes('lad') || s.includes('heiz'))
    return { label: raw || 'Lädt', tone: 'charging' };
  if (s.includes('idle') || s.includes('bereit') || s.includes('standby'))
    return { label: raw || 'Bereit', tone: 'idle' };
  return { label: raw, tone: 'unknown' };
}
